import type { HemBenchmark } from '@/pages/blueprint-lite/types';
import { fxToAud } from '@/pages/mortgage/routes/Purchase/assessment/config/fx';
import { buildIncomeStream } from '@/pages/mortgage/routes/Purchase/assessment/mapper/fromSummary';

import { HEM_CLOSE_MARGIN } from './constant';

export type HemStatus = 'below' | 'close' | 'above';

/**
 * HEM minimum monthly living expense (AUD) for a household, or null when the
 * benchmarks have no rows for it (e.g. single with dependants today).
 * Unlike blueprint-lite's `lookupHem`, this matches the exact dependant count
 * (large families capped at the highest one in the data) and treats gaps between bands
 * (50,000 -> 50,001) as part of the lower band.
 */
export const findHemMinimum = (
  rows: HemBenchmark[],
  maritalStatus: unknown,
  dependants: unknown,
  annualIncome: number,
): number | null => {
  const status =
    maritalStatus === 'married' || maritalStatus === 'defacto'
      ? 'married_defacto'
      : 'single';
  const forStatus = rows.filter((r) => r.maritalStatus === status);
  if (!forStatus.length) return null;

  // Cap large families at the highest count in the data, but only when the
  // data has dependant rows at all: single + children must not fall back to
  // the no-children benchmark.
  const maxDeps = Math.max(...forStatus.map((r) => r.dependants));
  const asked = Math.max(Math.floor(Number(dependants) || 0), 0);
  const deps = maxDeps > 0 ? Math.min(asked, maxDeps) : asked;
  const bands = forStatus
    .filter((r) => r.dependants === deps)
    .sort((a, b) => a.incomeMin - b.incomeMin);
  if (!bands.length) return null;

  // Last band whose lower bound the income has reached.
  const band = [...bands].reverse().find((b) => annualIncome >= b.incomeMin);
  return (band ?? bands[0]).minimumMonthlyExpenseAud;
};

/** Red / yellow / green bucket for a monthly total against the HEM minimum. */
export const hemStatus = (total: number, minimum: number): HemStatus => {
  if (total < minimum) return 'below';
  if (total <= minimum * (1 + HEM_CLOSE_MARGIN)) return 'close';
  return 'above';
};

/**
 * The applicant's gross annual income in AUD (base + bonus + allowance +
 * commission + stock, no shading), from the purchase application summary.
 * 'no-income' when none is entered, 'no-fx' when the salary currency has no
 * live FX rate (the forex feed is down or lacks it).
 */
export const applicantGrossAnnualAud = (
  incomeDetail: Record<string, unknown> | null | undefined,
  fxRates: Record<string, number> | undefined,
): number | 'no-income' | 'no-fx' => {
  if (!incomeDetail) return 'no-income';
  try {
    const s = buildIncomeStream(incomeDetail, (c) => fxToAud(c, fxRates));
    const gross = s.baseSalary + s.bonus + s.allowance + s.commission + s.stock;
    return gross > 0 ? gross : 'no-income';
  } catch {
    return 'no-fx'; // FxRateMissingError: can't express the income in AUD
  }
};

/** What the expenses step shows: a minimum to compare against, or why not. */
export type HemBenchmarkState = { minimum: number } | { unavailable: string };
